import { LockOpenIcon, LockSimpleIcon, PlayIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import React from 'react'

import { getSourceType } from '../../../../AttachmentCard'
import type { PaymentStatus } from '../../types'

export interface LockBadgeProps {
  paymentStatus?: PaymentStatus
  mimeType?: string
  /**
   * Overrides the `mimeType`-derived playable check. Lets a caller that
   * already knows per-item playability (e.g. `GalleryThumbnail`, checking a
   * carousel item's own mimeType rather than the card's single `mimeType`)
   * skip re-deriving it.
   */
  isPlayable?: boolean
  /** Whether the item is still locked. Play only ever shows once unlocked —
   * a locked item always shows the lock, even for otherwise-playable media. */
  isLocked: boolean
}

/**
 * Centered 48x48 lock badge that overlays the thumbnail of any locked attachment.
 * Uses `bg-black/30`. Stays `pointer-events-none` so clicks pass through to
 * the underlying player/toggle; the play state highlights via `group-hover`
 * (same `bg-black/40` as the gallery's prev/next arrows) — the ancestor
 * providing `group` must wrap the actual clickable/hoverable player area.
 *
 * For playable media (video/audio) that's unlocked, the badge shows a play
 * icon instead of a lock — matching the convention used by `GalleryThumbnail`.
 */
export const LockBadge: React.FC<LockBadgeProps> = ({
  paymentStatus,
  mimeType,
  isPlayable,
  isLocked,
}) => {
  const sourceType = mimeType ? getSourceType(mimeType) : undefined
  const resolvedIsPlayable =
    isPlayable ?? (sourceType === 'video' || sourceType === 'audio')

  const Icon =
    resolvedIsPlayable && !isLocked
      ? PlayIcon
      : paymentStatus === 'paid'
        ? LockOpenIcon
        : LockSimpleIcon
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div
        className={classNames(
          'flex size-12 items-center justify-center rounded-full bg-black/30 text-white',
          Icon === PlayIcon && 'group-hover:bg-black/40'
        )}
      >
        <Icon className="size-6" weight="fill" />
      </div>
    </div>
  )
}
